import React, { useContext, useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoaderContext } from "../../../../App";
import { ASSESSMENTSCORINGMETHODS, LS_AUTHORITY_KEY } from "../../../../core/constants/common";
import FormService from "../../../../core/service/FormService";
import "./FormSectionModal.scss";

// Form types for which the form builder shows scoring method and weightage on a section
const SCORED_FORM_TYPES = ['ASSESSMENT', 'GENERAL'];

const SCORING_METHOD_LABEL_KEYS = {
	NONE: 'None.label',
	AVERAGE: 'Average.label',
	SUM: 'Sum.label'
};

// Creates a section, or a sub-section when isSubsection is set, through the form builder section API
function FormSectionModal({ t, show, formId, formType, isSubsection, parentSections, defaultParentId, onHide, onCreated }) {
	const { setLoaderDisplay } = useContext(LoaderContext);

	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [parentId, setParentId] = useState('');
	const [scoringMethod, setScoringMethod] = useState('NONE');
	const [weightage, setWeightage] = useState('');
	const [uomRequired, setUomRequired] = useState(false);
	const [uom, setUom] = useState('');
	const [outputUnits, setOutputUnits] = useState([]);
	const [validated, setValidated] = useState(false);
	const [formDetails, setFormDetails] = useState(null);

	// Unknown form type still shows scoring, as the DCE forms are assessment forms
	const resolvedFormType = formDetails?.formType ?? formType;
	const showScoring = !resolvedFormType || SCORED_FORM_TYPES.includes(resolvedFormType);

	// The section API rejects a section without the form's language, so load it like the form builder does
	useEffect(() => {
		if (!show || !formId) return;

		FormService.getFormDetails(formId).then(res => {
			if (res.status !== 200) return;

			setFormDetails(res.data);
		}).catch(err => { });
	}, [show, formId]);

	useEffect(() => {
		if (!show) return;
		setTitle('');
		setDescription('');
		setParentId(defaultParentId ?? '');
		setScoringMethod('NONE');
		setWeightage('');
		setUomRequired(false);
		setUom('');
		setValidated(false);
	}, [show, defaultParentId]);

	useEffect(() => {
		if (!uomRequired || outputUnits.length > 0) return;

		const companyId = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY))?.profile?.company?.id;
		FormService.getFormUnitsOfMeasure(companyId).then(res => {
			if (!res.data?.length) return;

			// One entry per output unit, preferring the entry whose name equals the unit code (form builder logic)
			const unitsMap = new Map();
			res.data.forEach(el => {
				const existing = unitsMap.get(el.outputUnit);
				const isExactLabelMatch = el.outputUnitName === el.outputUnit;
				if (!existing || (isExactLabelMatch && existing.label !== existing.value)) {
					unitsMap.set(el.outputUnit, { id: el.id, label: el.outputUnitName, value: el.outputUnit });
				}
			});
			setOutputUnits([...unitsMap.values()]);
		}).catch(err => { });
	}, [uomRequired, outputUnits.length]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setValidated(true);
		if (!e.currentTarget.checkValidity()) return;

		// Same payload the form builder sends when it creates a section
		const payload = {
			title: title.trim(),
			description: description.trim(),
			layout: 'INLINE',
			displayTitle: true,
			sortOrder: 100
		};
		if (isSubsection) payload.parentSection = { id: parseInt(parentId, 10) };
		if (showScoring) {
			payload.weightage = weightage;
			payload.scoringMethod = scoringMethod;
		}
		if (uomRequired) payload.uom = uom;
		const languageId = formDetails?.selectedLanguage?.id;
		if (languageId) payload.selectedLanguage = { id: languageId };

		setLoaderDisplay(true);
		FormService.createFormSection(formId, payload).then(res => {
			if (res.status !== 200 && res.status !== 201) return;

			toast.success(
				t(isSubsection ? 'SubSectionCreatedSuccessfully.label' : 'SectionCreatedSuccessfully.label'),
				{ position: toast.POSITION.TOP_RIGHT }
			);
			// onCreated may return a promise (the parent reloading the form); keep the loader until it settles
			return onCreated?.(res.data, isSubsection ? payload.parentSection.id : null);
		}).catch(err => { }).finally(() => setLoaderDisplay(false));
	};

	const renderLabel = (htmlFor, label, required = true) => (
		<label className="form-section-modal__label" htmlFor={htmlFor}>
			{label}
			{required && <span className="form-section-modal__required" aria-hidden="true">*</span>}
		</label>
	);

	const renderScoring = () => (
		<div className="form-section-modal__scoring">
			<div>
				{renderLabel('formSectionScoring', t('ScoreingMethod.label'), false)}
				<Form.Select id="formSectionScoring" className="form-section-modal__select"
					value={scoringMethod} onChange={(e) => setScoringMethod(e.target.value)}>
					{ASSESSMENTSCORINGMETHODS.map(method => (
						<option key={`formSectionScoring-${method.key}`} value={method.key}>
							{t(SCORING_METHOD_LABEL_KEYS[method.key])}
						</option>
					))}
				</Form.Select>
				{scoringMethod !== 'NONE' && (
					<small className="form-section-modal__hint">
						{t('Scoreofthheswctioncalculated.label')}
						<span className="text-lower">{t(SCORING_METHOD_LABEL_KEYS[scoringMethod])}</span>{' '}
						{scoringMethod === 'AVERAGE' && <span>{t('score.label')}</span>}
						{t('ofallthequestionsinsection.label')}
					</small>
				)}
			</div>
			<div>
				{renderLabel('formSectionWeightage', t('WeightagePercent.label'), false)}
				<Form.Control id="formSectionWeightage" type="number" min={0} max={100}
					className="form-section-modal__input"
					value={weightage} onChange={(e) => setWeightage(e.target.value)} />
				<small className="form-section-modal__hint">
					{t('SectionWeightageOfFormScore.label', { weightage: weightage })}
				</small>
			</div>
		</div>
	);

	const renderUom = () => (
		<div className="form-section-modal__uom">
			<Form.Check id="formSectionUom" type="checkbox" className="form-section-modal__check"
				label={t('UnitOfMeasure.label')}
				checked={uomRequired} onChange={(e) => setUomRequired(e.target.checked)} />
			{uomRequired && (
				<Typeahead
					id="formSectionUomSelect"
					className="form-section-modal__uom-select"
					size="sm"
					clearButton
					labelKey="label"
					options={outputUnits}
					placeholder={t('SelectUom.label')}
					selected={outputUnits.filter(unit => unit.value === uom)}
					onChange={(selected) => setUom(selected[0]?.value ?? '')} />
			)}
		</div>
	);

	// Same field order and look as the form builder's section modal
	const renderFields = () => (
		<>
			<div className="form-section-modal__field">
				{renderLabel('formSectionTitle', t(isSubsection ? 'SubsectionTitle.label' : 'SectionTitle.label'))}
				<Form.Control id="formSectionTitle" className="form-section-modal__input" required
					value={title} onChange={(e) => setTitle(e.target.value)} />
			</div>
			{isSubsection && (
				<div className="form-section-modal__field">
					{renderLabel('formSectionParent', t('ParentSection.label'))}
					<Form.Select id="formSectionParent" className="form-section-modal__select" required
						value={parentId} onChange={(e) => setParentId(e.target.value)}>
						<option value="">{t('Select.label')}</option>
						{parentSections?.map(section => (
							<option key={`formSectionParent-${section.id}`} value={section.id}>{section.title}</option>
						))}
					</Form.Select>
				</div>
			)}
			<div className="form-section-modal__field">
				{renderLabel('formSectionDescription', t('Description.label'))}
				<Form.Control id="formSectionDescription" as="textarea" rows={5}
					className="form-section-modal__textarea" required
					value={description} onChange={(e) => setDescription(e.target.value)} />
			</div>
			{showScoring && renderScoring()}
			{renderUom()}
		</>
	);

	return (
		<Modal show={show} onHide={onHide} centered className="form-section-modal">
			<Form noValidate validated={validated} onSubmit={onSubmit}>
				<Modal.Header closeButton className="form-section-modal__header">
					<Modal.Title className="form-section-modal__title">
						{t(isSubsection ? 'CreateSubsection.label' : 'CreateSection.label')}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="form-section-modal__body">
					{renderFields()}
				</Modal.Body>
				<Modal.Footer className="form-section-modal__footer">
					<button type="button" className="form-section-modal__btn form-section-modal__btn--secondary" onClick={onHide}>
						{t('Cancel.label')}
					</button>
					<button type="submit" className="form-section-modal__btn form-section-modal__btn--primary"
						disabled={String(formDetails?.id) !== String(formId)}>
						{t('Save.label')}
					</button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
}

export default withTranslation()(FormSectionModal);
